// src/public/rsvp/parts/AttendanceAccordion.jsx
import React, { useMemo, useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Icon from '@mui/material/Icon';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';

import { compactAccordionSx } from './rsvpCompactSx';

function buildConfirm(choice, insights = {}) {
  const fun = {
    YES: { title: 'Katılım', headline: 'Harika! ✨ Seansımız için çok heyecanlıyız.' },
    NO: {
      title: 'Katılım',
      headline: 'Üzüldük ama sorun değil 🤍 Bir sonraki seansta enerjiyi iki katına çıkarırız!',
    },
  }[choice];

  const warnings = [];
  if (insights?.isFirstSession) warnings.push('Bu ders paketinizin ilk dersi olacak.');
  if (insights?.willConsume) warnings.push('Bu ders paket hakkınızdan düşülecek.');
  if (typeof insights?.remainingAfter === 'number') {
    if (insights.remainingAfter <= 0) warnings.push('Bu dersten sonra paket hakkınız bitecek.');
    else warnings.push(`Bu dersten sonra kalan hakkınız: ${insights.remainingAfter}`);
  }
  if (insights?.hasDebt) warnings.push('Ödenmemiş bir bakiyeniz görünüyor.');

  return { ...fun, warningText: warnings.join(' ') };
}

export function AttendanceAccordion({
  remainingChanges,
  saving,
  canAnswer,
  onAnswer,
  paymentReminder,
  attendanceInsights,
  currentStatus,
}) {
  const hasDebt = !!paymentReminder?.hasDebt;

  const status = (currentStatus || '').toUpperCase();
  const hasStatus = status === 'YES' || status === 'NO';

  const [isEditing, setIsEditing] = useState(() => !hasStatus);
  useEffect(() => setIsEditing(!hasStatus), [hasStatus]);

  const [openDialog, setOpenDialog] = useState(false);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [dialogTone, setDialogTone] = useState('warning');
  const [dialogTitle, setDialogTitle] = useState('Katılım');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogSubtitle, setDialogSubtitle] = useState('');

  const mergedInsights = useMemo(
    () => ({ ...attendanceInsights, hasDebt: attendanceInsights?.hasDebt ?? hasDebt }),
    [attendanceInsights, hasDebt]
  );

  function closeDialog() {
    if (busy) return;
    setOpenDialog(false);
    setChoice(null);
  }

  async function handleClick(nextChoice) {
    if (!canAnswer) return;
    if (busy || saving) return;

    const model = buildConfirm(nextChoice, mergedInsights);

    setChoice(nextChoice);
    setOpenDialog(true);
    setBusy(true);

    setDialogTone(nextChoice === 'YES' ? 'success' : 'error');
    setDialogTitle(model.title || 'Katılım');
    setDialogMessage('Kaydediliyor…');
    setDialogSubtitle('');

    try {
      await onAnswer(nextChoice);
      setIsEditing(false);

      setDialogMessage(model.headline);
      const tail = 'Katılım durumunuz işaretlendi.';
      setDialogSubtitle(model.warningText ? `${model.warningText} • ${tail}` : tail);

      setDialogTone(nextChoice === 'YES' ? 'success' : 'error');
    } catch (e) {
      const msg =
        (e && (e.userMessage || e.message)) ||
        'İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.';
      setDialogTone('error');
      setDialogMessage(msg);
      setDialogSubtitle('');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!openDialog) return;
    if (busy) return;

    const isSuccess =
      dialogMessage === 'Harika! ✨ Seansımız için çok heyecanlıyız.' ||
      dialogMessage ===
        'Üzüldük ama sorun değil 🤍 Bir sonraki seansta enerjiyi iki katına çıkarırız!';

    if (!isSuccess) return;

    const t = setTimeout(() => {
      setOpenDialog(false);
      setChoice(null);
    }, 2600);

    return () => clearTimeout(t);
  }, [openDialog, busy, dialogMessage]);

  const uiBusy = !!saving || busy;

  // ✅ Kartı primary hissettiren çok hafif accent
  const accentSx = (t) => ({
    borderColor: t.palette.mode === 'dark' ? alpha('#c7a56a', 0.25) : alpha('#b17c21', 0.2),
    boxShadow: 'none',
  });

  const alertSx = {
    mb: 0.6,
    borderRadius: '8px',
    py: 0.55,
    '& .MuiAlert-message': { py: 0, fontSize: 12.5, lineHeight: 1.25 },
  };

  return (
    <>
      <Accordion defaultExpanded sx={(t) => ({ ...compactAccordionSx, ...accentSx(t) })}>
        <AccordionSummary
          expandIcon={<Icon sx={{ fontSize: 18 }}>expand_more</Icon>}
          sx={(t) => ({
            // summary biraz daha “header” gibi dursun
            background: t.palette.mode === 'dark' ? alpha('#c7a56a', 0.06) : alpha('#b17c21', 0.06),
          })}
        >
          <MDBox>
            <MDTypography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.1 }}>
              Katılım Seçimi
            </MDTypography>
            <MDTypography sx={{ fontSize: 11.5, opacity: 0.72 }}>
              Kalan değişiklik: <b>{remainingChanges}</b>
            </MDTypography>
          </MDBox>
        </AccordionSummary>

        <AccordionDetails>
          {/* Ödeme uyarısı */}
          {paymentReminder ? (
            <Alert severity={hasDebt ? 'warning' : 'info'} sx={alertSx}>
              {paymentReminder?.message ||
                (hasDebt ? 'Ödenmemiş bakiyeniz bulunmaktadır.' : 'Bakiye görünmüyor.')}
            </Alert>
          ) : null}

          {/* Katılım uyarısı */}
          <Alert severity={remainingChanges <= 0 ? 'info' : 'warning'} sx={alertSx}>
            {remainingChanges <= 0
              ? 'Bu linkte değişiklik hakkı kalmamış. Değişiklik için eğitmen ile iletişime geçebilirsiniz'
              : hasStatus && !isEditing
              ? 'Değiştirmek isterseniz “Değiştir”e basın.'
              : 'Lütfen katılım durumunuzu seçin.'}
          </Alert>

          {/* Status kartı */}
          {hasStatus && !isEditing ? (
            <MDBox
              sx={(t) => ({
                mb: 0.6,
                px: 1,
                py: 0.75,
                borderRadius: '8px',
                border: `1px solid ${alpha(
                  t.palette.text.primary,
                  t.palette.mode === 'dark' ? 0.12 : 0.1
                )}`,
                background:
                  status === 'YES'
                    ? alpha(t.palette.success.main, 0.1)
                    : alpha(t.palette.error.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              })}
            >
              <MDTypography sx={{ fontSize: 12.5, fontWeight: 900 }}>
                {status === 'YES'
                  ? 'Katılacağım olarak işaretlediniz.'
                  : 'Katılamayacağınızı bildirdiniz.'}
              </MDTypography>

              <MDButton
                variant="text"
                color={status === 'YES' ? 'success' : 'error'}
                size="small"
                disabled={!canAnswer || uiBusy || remainingChanges <= 0}
                onClick={() => setIsEditing(true)}
                sx={{ textTransform: 'none', fontWeight: 900, minWidth: 0, px: 1 }}
              >
                Değiştir
              </MDButton>
            </MDBox>
          ) : null}

          {/* ✅ Sticky CTA: sadece edit modda */}
          {isEditing ? (
            <MDBox
              sx={(t) => ({
                position: { xs: 'sticky', sm: 'static' },
                bottom: { xs: 8, sm: 'auto' },
                zIndex: 5,
                mt: 0.25,

                // sticky bar görünümü
                p: { xs: 0.75, sm: 0 },
                borderRadius: '8px',
                background:
                  t.palette.mode === 'dark'
                    ? alpha(t.palette.background.default, 0.78)
                    : alpha('#fff', 0.78),
                backdropFilter: { xs: 'saturate(140%) blur(10px)', sm: 'none' },
                border: {
                  xs: `1px solid ${
                    t.palette.mode === 'dark'
                      ? alpha(t.palette.common.white, 0.1)
                      : alpha(t.palette.common.black, 0.08)
                  }`,
                  sm: 'none',
                },
              })}
            >
              <MDBox display="flex" gap={0.75} flexWrap="wrap">
                <MDButton
                  fullWidth
                  variant="contained"
                  color="success"
                  disabled={!canAnswer || uiBusy}
                  onClick={() => handleClick('YES')}
                  startIcon={<Icon sx={{ fontSize: 18 }}>check</Icon>}
                  sx={{
                    flex: 1,
                    minWidth: 170,
                    py: 0.9,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 900,
                  }}
                >
                  Katılacağım
                </MDButton>

                <MDButton
                  fullWidth
                  variant="contained"
                  color="error"
                  disabled={!canAnswer || uiBusy}
                  onClick={() => handleClick('NO')}
                  startIcon={<Icon sx={{ fontSize: 18 }}>close</Icon>}
                  sx={{
                    flex: 1,
                    minWidth: 170,
                    py: 0.9,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 900,
                  }}
                >
                  Katılamayacağım
                </MDButton>

                {hasStatus ? (
                  <MDButton
                    fullWidth
                    variant="text"
                    color="secondary"
                    disabled={uiBusy}
                    onClick={() => setIsEditing(false)}
                    sx={{ flexBasis: '100%', mt: 0.1, textTransform: 'none', fontWeight: 900 }}
                  >
                    Vazgeç
                  </MDButton>
                ) : null}
              </MDBox>
            </MDBox>
          ) : null}

          {uiBusy ? (
            <MDBox mt={0.75} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <MDTypography sx={{ fontSize: 12.5, opacity: 0.8 }}>Kaydediliyor…</MDTypography>
            </MDBox>
          ) : null}
        </AccordionDetails>
      </Accordion>

      <ConfirmDialog
        open={openDialog}
        title={dialogTitle}
        message={dialogMessage}
        subtitle={dialogSubtitle}
        items={null}
        tone={dialogTone}
        busy={busy}
        showConfirm={false}
        cancelText="Kapat"
        onCancel={closeDialog}
        onClose={closeDialog}
      />
    </>
  );
}
